import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
} from '@mui/material';
import {
  Storage,
  Security,
  Speed,
  Public,
  CheckCircle,
  ArrowForward,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import CTASection from '../components/common/CTASection';

const DataCenters = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const features = [
    { icon: <Security />, title: 'Enterprise Security', desc: '24/7 surveillance, biometric access, and security guards' },
    { icon: <Speed />, title: 'High Performance', desc: '100Gbps connectivity with 99.999% uptime SLA' },
    { icon: <Public />, title: 'Global Reach', desc: 'Connected to major cloud providers and networks' },
    { icon: <Storage />, title: 'Scalable Solutions', desc: 'From single rack to full private suites' },
  ];

  const locations = [
    { city: 'New York', country: 'USA', capacity: '15MW', tier: 'Tier IV', status: 'Operational' },
    { city: 'London', country: 'UK', capacity: '12MW', tier: 'Tier III+', status: 'Operational' },
    { city: 'Singapore', country: 'Singapore', capacity: '20MW', tier: 'Tier IV', status: 'Operational' },
    { city: 'Frankfurt', country: 'Germany', capacity: '10MW', tier: 'Tier III+', status: 'Operational' },
    { city: 'Tokyo', country: 'Japan', capacity: '8MW', tier: 'Tier III', status: 'Coming Soon' },
    { city: 'São Paulo', country: 'Brazil', capacity: '6MW', tier: 'Tier III', status: 'Coming Soon' },
  ];

  return (
    <Box>
      <PageHeader 
        title="Data Centers" 
        subtitle="Secure, scalable, and highly available infrastructure"
      />

      {/* Stats Section */}
      <Box sx={{ py: 6, bgcolor: 'background.default' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {[
              { number: '50+', label: 'Global Data Centers', color: '#0066CC' },
              { number: '500,000+', label: 'Sq Ft Total Space', color: '#00A3FF' },
              { number: '200+', label: 'MW Total Power', color: '#00C853' },
              { number: '99.999%', label: 'Uptime SLA', color: '#FF6B00' },
            ].map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 700 }}>
                  Enterprise-Grade Data Center Solutions
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Our data centers are designed to meet the most demanding requirements 
                  for security, reliability, and performance.
                </Typography>
                <Stack spacing={2}>
                  {features.map((feature, index) => (
                    <Stack direction="row" spacing={2} key={index}>
                      <Avatar sx={{ bgcolor: theme.palette.primary.main }}>{feature.icon}</Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.desc}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/contact')}
                  sx={{ mt: 4 }}
                >
                  Request a Quote
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop"
                  alt="Data Center"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: theme.shadows[8],
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Locations */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 700 }}>
              Global Locations
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
              Strategically positioned data centers worldwide
            </Typography>
          </motion.div>

          <Grid container spacing={3}>
            {locations.map((location, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {location.city}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {location.country}
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      <Chip label={`Capacity: ${location.capacity}`} size="small" />
                      <Chip label={`Tier: ${location.tier}`} size="small" />
                      <Chip 
                        label={location.status} 
                        size="small"
                        color={location.status === 'Operational' ? 'success' : 'warning'}
                      />
                    </Stack>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <CTASection 
        title="Secure Your Infrastructure" 
        subtitle="Get premium data center space with carrier-neutral connectivity"
        buttonText="Explore Locations"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default DataCenters;